"use client";

import React, { useEffect, useRef } from "react";
import {
  RefreshCw,
  Bell,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

import { useProfileDropdown } from "@/components/hooks/profileDropdown/useProfileDropDown.hook";
import useHeader from "@/hooks/useHeader";
import { Separator } from "@/components/ui/separator";
import BlueNotificationIcon from "@/components/ui/icons/BlueNotificationIcon";
import EmptyNotificationIcon from "@/components/ui/icons/EmptyNotificationIcon";
import { useNotificationList } from "@/hooks/notification/useNotificationList.hooks";

interface ContractorNotificationDropdownProps {
  unreadCount?: number;
}

const ContractorNotificationDropdown = ({
  unreadCount: propUnreadCount = 0,
}: ContractorNotificationDropdownProps) => {
  const {
    isMounted,
    isLoggingOut,
    // Dropdown state
    isOpen,
    setIsOpen,
  } = useProfileDropdown();

  const { deviceWidth } = useHeader();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const MotionButton = motion(Button);
  const {
    notifications,
    loading: notificationsLoading,
    isLoadingMore,
    error: notificationsError,
    hasMore,
    fetchMoreNotifications,
    refreshNotifications,
    totalCount,
    handleMarkAllAsRead,
    handleNotificationClick,
    expandedIds,
    toggleExpand,
  } = useNotificationList({ userType: "Contractor", isOpen });

  const unreadCount = typeof propUnreadCount === "number" ? propUnreadCount : totalCount;

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const timer = setTimeout(() => {
      const sentinel = sentinelRef.current;
      const container = scrollContainerRef.current;
      if (!sentinel || !container || !hasMore || notificationsLoading || isLoadingMore || !isOpen) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry?.isIntersecting && hasMore && !notificationsLoading && !isLoadingMore) {
            fetchMoreNotifications();
          }
        },
        {
          root: container,
          rootMargin: "50px",
          threshold: 0,
        }
      );

      observer.observe(sentinel);

      return () => {
        observer.disconnect();
      };
    }, 100);

    return () => clearTimeout(timer);
  }, [hasMore, notificationsLoading, isLoadingMore, fetchMoreNotifications, isOpen, notifications.length]);

  if (!isMounted) {
    return null; 
  }

  return (
    <DropdownMenu 
      modal={false} 
      open={isOpen || isLoggingOut} 
      onOpenChange={(open) => {
        if (!isLoggingOut) {
          setIsOpen(open);
        }
      }}
    >
      <DropdownMenuTrigger
        asChild
        className="items-center !relative gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-transparent !px-2 focus-visible:ring-offset-0 focus-visible:rounded-lg flex w-10 h-10 mr-3 cursor-pointer rounded-lg"
        aria-label="Profile menu"
      >
        <MotionButton
          whileTap={{ scale: 0.95, y: 2 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
          variant="outline"
          className="relative flex h-11 w-11 items-center justify-center"
          size="lg"
          data-testid="notification-bell"
        >
          {unreadCount > 0 && (
            <>
              <span
                className="pointer-events-none absolute -top-2 -left-1 inline-flex h-5 w-5 animate-ping rounded-full bg-[#FF5A65]/40"
                aria-hidden="true"
              />
              <span
                className="pointer-events-none absolute -top-2 -left-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF5A65] text-[10px] font-semibold text-white shadow-sm"
                data-testid="notification-badge"
              >
                {unreadCount}
              </span>
            </>
          )}
          <Bell className="!h-5 !w-5" size={24} />
          <span className="sr-only">Notifications</span>
        </MotionButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        alignOffset={0}
        sideOffset={5}
        className="flex flex-col p-0 w-[400px] max-h-[550px]"
        align={deviceWidth !== null && deviceWidth < 1200 ? "end" : "center"}
      >
      

        <DropdownMenuGroup className="py-2 px-4 shrink-0">
          <DropdownMenuItem
            className="flex font-medium leading-[22px] items-center gap-2  cursor-pointer pb-3 hover:!bg-transparent focus:!bg-transparent"
          >
            <div className="flex items-center gap-2 justify-between w-full pb-1">
                <span className="text-base font-semibold">Notifications</span>
                <div className="flex items-center gap-2">
                  {notificationsLoading && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  )}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAllAsRead();
                    }}
                    className="text-base text-primary font-semibold hover:underline cursor-pointer"
                  >
                    Mark all as read
                  </button>
                </div>
            </div>
          </DropdownMenuItem>
          <Separator className="my-0 bg-gray-100" />
        </DropdownMenuGroup>
       

      
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto py-1 relative"
        >
          {notificationsLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {notificationsLoading && notifications.length > 0 && (
                <div className="flex items-center justify-center py-2 sticky top-0 bg-white z-10">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              )}
              <DropdownMenuGroup className="py-2 space-y-1">
                {notifications.length === 0 && !notificationsLoading ? (
                  <DropdownMenuItem className="flex items-center justify-center py-8 hover:!bg-transparent focus:!bg-transparent">
                    <div className="flex flex-col items-center gap-3">
                      <EmptyNotificationIcon />
                      <p className="text-xs text-slate-500 font-normal">There&apos;s is no notifications here.</p>
                    </div>
                  </DropdownMenuItem>
                ) : (
                  notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification, () => setIsOpen(false))}
                  className={`flex font-medium leading-[22px] items-center gap-2 px-3 py-3 cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                    !notification.isRead ? 'bg-gray-100' : ''
                  }`}
                >
                      <div className="flex items-start gap-3 justify-between w-full">
                        <div>
                          <BlueNotificationIcon />
                        </div>
                        <div className="flex flex-col gap-1 flex-1">
                          <h3 className="text-sm font-semibold text-slate-900">{notification.title}</h3>
                          <div className="text-xs text-slate-500 font-normal">
                            <p
                              className={
                                !expandedIds.includes(notification.id) ? "line-clamp-2" : ""
                              }
                            >
                              {notification.description}
                            </p>
                            {notification.description.length > 103 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(notification.id);
                                }}
                                className="text-[11px] text-primary font-semibold hover:underline focus:outline-none block mt-1 cursor-pointer"
                              >
                                {expandedIds.includes(notification.id) ? "See less" : "See more"}
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 font-medium">{notification.notificationDays}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuGroup>

              {/* Infinite scroll sentinel */}
              <div
                ref={sentinelRef}
                aria-hidden="true"
                className="h-1 w-full"
              />
            
              {(notificationsLoading || isLoadingMore) && (
                <div className="flex items-center justify-center py-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-slate-500">Loading...</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ContractorNotificationDropdown;
